/**
 * <QuickActionGrid> · ADR-FO-1 §Front Office Quick Actions
 * ────────────────────────────────────────────────────────────────
 * Large touch tiles for high-frequency operational actions. Each tile:
 * icon + label + optional badge + optional count + optional warning tone.
 * Optional keyboard shortcut hint. Zero business logic.
 */
import React, { useEffect } from "react";
import { getStatusToken } from "./index";

export function QuickActionGrid({
  actions = [],       // [{id, label, sublabel?, icon, count?, badge?, shortcut?,
                       //   onClick, warning?, disabled?, testid?, tone?}]
  columns,             // override default responsive columns
  disabled = false,    // disable ALL tiles
  testid = "quick-action-grid",
}) {
  // Wire keyboard shortcuts (AR-5). Only registers shortcuts that don't
  // conflict with typing (skips when target is input/textarea/contenteditable).
  useEffect(() => {
    const map = {};
    for (const a of actions) {
      if (a.shortcut && !a.disabled && !disabled) map[a.shortcut.toUpperCase()] = a;
    }
    if (Object.keys(map).length === 0) return;

    const handler = (e) => {
      const tgt = e.target;
      const tag = (tgt?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tgt?.isContentEditable) return;
      const key = (e.key || "").toUpperCase();
      const a = map[key];
      if (a) {
        e.preventDefault();
        a.onClick?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [actions, disabled]);

  const gridCols = columns
    ? `grid-cols-${columns}`
    : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5";

  return (
    <div data-testid={testid}
         className={`grid ${gridCols} gap-3`}>
      {actions.map(a => (
        <Tile key={a.id || a.testid || a.label}
                {...a}
                globalDisabled={disabled}
                testid={a.testid || `${testid}-${a.id || a.label.toLowerCase().replace(/\s+/g, "-")}`}/>
      ))}
    </div>
  );
}

function Tile({
  label, sublabel, icon: Icon, count, badge, shortcut,
  onClick, warning, disabled, globalDisabled, tone, testid,
}) {
  const isDisabled = disabled || globalDisabled;
  const statusKey = warning ? "attention" : (tone || "ready");
  const tok = getStatusToken(statusKey);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      data-testid={testid}
      className={[
        "relative flex flex-col items-start justify-between text-left",
        "min-h-[112px] w-full rounded-2xl border-2 p-4",
        "transition-all active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-300",
        tok.tone.bg, tok.tone.border, tok.tone.hoverBorder,
        isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
    >
      {/* Top row: icon + optional badge (top-right) */}
      <div className="w-full flex items-start justify-between">
        <div className="flex items-center gap-2">
          {Icon && (
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${tok.tone.bg} border ${tok.tone.border}`}>
              <Icon className={`w-5 h-5 ${tok.tone.text}`} strokeWidth={2.2}/>
            </span>
          )}
          {shortcut && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 border border-slate-300"
                    aria-label={`Shortcut: ${shortcut}`}
                    data-testid={testid ? `${testid}-shortcut` : undefined}>
              {shortcut}
            </span>
          )}
        </div>
        {badge && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-semibold"
                  data-testid={testid ? `${testid}-badge` : undefined}>
            {badge}
          </span>
        )}
      </div>

      {/* Label + sublabel */}
      <div className="mt-2 w-full">
        <div className={`text-[15px] font-semibold ${tok.tone.text} leading-tight`}>
          {label}
        </div>
        {sublabel && (
          <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
            {sublabel}
          </div>
        )}
      </div>

      {/* Count */}
      {typeof count === "number" && (
        <div className={`mt-2 text-[24px] font-bold leading-none ${tok.tone.text}`}
              data-testid={testid ? `${testid}-count` : undefined}>
          {count}
        </div>
      )}
    </button>
  );
}
